import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Box, Button, FormControl, Grid2, InputAdornment, InputLabel, MenuItem, Paper, Select, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { getEntities } from "../../api/entities";
import { getEntityManagementUnits } from "../../api/managementUnits";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../hooks/useFormStyles";
import { CreateCaptureForm, getCreateCaptureFormSchema } from "../../types/forms/capture";

interface AddCaptureFormProps {
  onSubmit: (values: CreateCaptureForm, chosenEntityId?: number) => Promise<void>;
  entityId?: number;
  managementUnitId?: number;
  crs: "wgs84" | "etrs89";
  onCrsChange: (newCrs: "wgs84" | "etrs89") => void;
}

export default function AddCaptureForm({ 
  onSubmit,
  entityId,
  managementUnitId,
  crs,
  onCrsChange,
}: Readonly<AddCaptureFormProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const navigate = useNavigate();

  const [entities, setEntities] = useState<{ id: number; name: string }[]>([]);
  const [systems, setSystems] = useState<{ id: number; name: string }[]>([]);
  const formStyles = useFormStyles(true);
  
  const [selectedEntityId, setSelectedEntityId] = useState<number | undefined>(entityId);

  const validationSchema = getCreateCaptureFormSchema(t);

  const methods = useForm({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      managementUnitId: managementUnitId ?? undefined,
      name: "",
      aquaEpsDesignation: "",
      entityDesignation: "",
      licensingDesignation: "",
      licensingDesignationSystem: null,
      coordinates:
      crs === "etrs89"
        ? { crs: "ETRS89", xETRS: undefined, yETRS: undefined, zETRS: null }
        : { crs: "WGS84", longitude: undefined, latitude: undefined, elevation: null },
    },
  })

  useEffect(() => {
    const fetchEntities = async () => {
      try {
        const data = await getEntities(true);

        if (entityId) {
          const found = data.find((e: any) => e.id === entityId);
          if (found) {
            setEntities([{ id: found.id, name: found.name }]);
          }
        } else {
          setEntities(
            data.map((e: any) => ({ id: e.id, name: e.name }))
          );
        }
      } catch (error) {
        handleError(error);
      }
    };

    fetchEntities();
  }, [entityId]);

  // Load management units when the selected entity changes.
  useEffect(() => {
    const fetchManagementUnits = async () => {
      try {
        const idToUse = selectedEntityId;

        if (!idToUse) {
          setSystems([]);
          return;
        }

        const data = await getEntityManagementUnits(idToUse);

        setSystems(
          data.map((s: any) => ({ id: s.id, name: s.name }))
        );
      } catch (error) {
        handleError(error);
      }
    };

    fetchManagementUnits();
  }, [selectedEntityId]);

  // Reset coordinate fields when the CRS changes.
  useEffect(() => {
    if (crs === "etrs89") {
      methods.reset({
        ...methods.getValues(),
        coordinates: {
          crs: "ETRS89",
          xETRS: undefined,
          yETRS: undefined,
          zETRS: null,
        },
      });
    } else {
      methods.reset({
        ...methods.getValues(),
        coordinates: {
          crs: "WGS84",
          longitude: undefined,
          latitude: undefined,
          elevation: null,
        },
      });
    }
  }, [crs]);

  const internalSubmit = async (values: CreateCaptureForm) => {
    try {
      if (!selectedEntityId) {
        enqueueSnackbar(
          t("captures.capture.general.identification.errors.entity-required"),
          { variant: "error" }
        );
        return;
      }
      await onSubmit(values, selectedEntityId);
    } catch (error) {
      handleError(error);
    }
  };

  let managementUnitOptions: React.ReactNode;

  if (managementUnitId) {
    managementUnitOptions = (
      <MenuItem value={managementUnitId}>
        {systems.find((s) => s.id === managementUnitId)?.name ??
          `${managementUnitId}`}
      </MenuItem>
    );
  } else if (systems.length > 0) {
    managementUnitOptions = systems.map((system) => (
      <MenuItem key={system.id} value={system.id}>
        {system.name}
      </MenuItem>
    ));
  } else {
    managementUnitOptions = (
      <MenuItem disabled>
        {t("generic.no-options")}
      </MenuItem>
    );
  }

  const locationKey =
    crs === "etrs89"
      ? "coordinates.zETRS"
      : "coordinates.elevation";

  return (
    <FormProvider {...methods}>
      <Box
        sx={{ p: 2 }}
        component="form"
        onSubmit={(e) => {
          methods.handleSubmit(internalSubmit)(e);
        }}
      >
        <Grid2 container spacing={2}>
          {/* Entity */}
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              label={`${t("captures.capture.general.identification.entity")} *`}
              value={selectedEntityId ?? ""}
              onChange={(e) => {
                const val = e.target.value ? Number.parseInt(e.target.value, 10) : undefined;
                setSelectedEntityId(val);
                methods.setValue("managementUnitId", undefined);
              }}
              disabled={!!entityId}
              sx={{ ...formStyles }}
            >
              {entityId ? (
                <MenuItem value={entityId}>
                  {entities.find((e) => e.id === entityId)?.name ?? `Entidade #${entityId}`}
                </MenuItem>
              ) : (
                entities.map((entity) => (
                  <MenuItem key={entity.id} value={entity.id}>
                    {entity.name}
                  </MenuItem>
                ))
              )}
            </TextField>
          </Grid2>

          {/* Management Units */}
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Controller
              name="managementUnitId"
              control={methods.control}
              render={({ field }) => (
                <TextField
                  select
                  fullWidth
                  label={t("captures.capture.general.identification.management-unit")}
                  {...field}
                  value={field.value ?? managementUnitId ?? ""}
                  onChange={(e) => {
                    const val = e.target.value ? Number.parseInt(e.target.value, 10) : undefined;
                    field.onChange(val);
                  }}
                  disabled={!!managementUnitId || !selectedEntityId}
                  helperText={
                    selectedEntityId
                      ? undefined
                      : t("captures.capture.general.identification.helper-text")
                  }
                  sx={{ ...formStyles }}
                >
                  {managementUnitOptions}
                </TextField> 
              )}
            />
          </Grid2>

          <Grid2 size={{xs: 12}}>
            <TextField
              fullWidth
              variant="outlined"
              label={`${t("captures.capture.general.identification.name")} *`}
              sx={{ ...formStyles }}
              {...methods.register("name")}
              error={!!methods.formState.errors.name}
              helperText={methods.formState.errors.name?.message}
            />
          </Grid2>
          <Grid2 size={{xs: 6}}>
            <TextField
              fullWidth
              variant="outlined"
              label={t("captures.capture.general.identification.entity-desig")}
              sx={{ ...formStyles }}
              {...methods.register("entityDesignation")}
            />
          </Grid2>
          <Grid2 size={{xs: 6}}>
            <TextField
              fullWidth
              variant="outlined"
              label={t("captures.capture.general.identification.aquaEsp-desig")}
              sx={{ ...formStyles }}
              {...methods.register("aquaEpsDesignation")}
            />
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <Controller
              name="licensingDesignationSystem"
              control={methods.control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={{ ...formStyles }}
                  error={!!methods.formState.errors.licensingDesignationSystem}
                >
                  <InputLabel>
                    {t("captures.capture.general.identification.licensing-system")}
                  </InputLabel>
                  <Select
                    {...field}
                    value={field.value ?? ""}
                    label={t("captures.capture.general.identification.licensing-system")}
                    onChange={(e) => field.onChange(e.target.value || null)}
                  >
                    <MenuItem value="SNIRH">SNIRH</MenuItem>
                    <MenuItem value="TUA">TUA</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <TextField
              fullWidth
              variant="outlined"
              label={t("captures.capture.general.identification.licensing-designation")}
              sx={{ ...formStyles }}
              {...methods.register("licensingDesignation")}
              error={!!methods.formState.errors.licensingDesignation}
              helperText={methods.formState.errors.licensingDesignation?.message}
            />
          </Grid2>

          {/* Location Details */}
          <Grid2 size={12}>
            <Grid2 container direction="column" spacing={2} sx={{ height: "100%" }}>
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h5" gutterBottom>
                    {t("captures.capture.general.location-details.title")}
                  </Typography>
                  <ToggleButtonGroup
                    value={crs}
                    exclusive
                    onChange={(_, newCrs) =>
                      newCrs && onCrsChange(newCrs)
                    }
                    size="small"
                    color="primary"
                  >
                    <ToggleButton value="etrs89">ETRS89</ToggleButton>
                    <ToggleButton value="wgs84">WGS84</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Reference Point */}
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t("captures.capture.general.location-details.reference-point")}
                  </Typography>
                  
                  <Grid2 container spacing={2}>
                    {crs === "etrs89" ? (
                      <>
                        <Grid2 size={6}>
                          <Controller
                            name="coordinates.xETRS"
                            control={methods.control}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                fullWidth
                                type="number"
                                inputProps={{ step: "any" }}
                                label={`${t("captures.capture.general.location-details.x-etrs")} *`}
                                variant="outlined"
                                sx={{ ...formStyles }}
                                slotProps={{
                                  input: {
                                    endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                                  },
                                }}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                              />
                            )}
                          />
                        </Grid2>
                        <Grid2 size={6}>
                          <Controller
                            name="coordinates.yETRS"
                            control={methods.control}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                fullWidth
                                type="number"
                                inputProps={{ step: "any" }}
                                label={`${t("captures.capture.general.location-details.y-etrs")} *`}
                                variant="outlined"
                                sx={{ ...formStyles }}
                                slotProps={{
                                  input: {
                                    endAdornment: <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>,
                                  },
                                }}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                              />
                            )}
                          />
                        </Grid2>
                      </>
                    ) : (
                      <>
                        <Grid2 size={6}>
                          <Controller
                            name="coordinates.longitude"
                            control={methods.control}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                fullWidth
                                type="number"
                                inputProps={{ step: "any" }}
                                label={`${t("captures.capture.general.location-details.longitude")} *`}
                                variant="outlined"
                                sx={{ ...formStyles }}
                                slotProps={{
                                  input: {
                                    endAdornment: <InputAdornment position="end">°</InputAdornment>,
                                  },
                                }}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                              />
                            )}
                          />
                        </Grid2>
                        <Grid2 size={6}>
                          <Controller
                            name="coordinates.latitude"
                            control={methods.control}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                fullWidth
                                type="number"
                                inputProps={{ step: "any" }}
                                label={`${t("captures.capture.general.location-details.latitude")} *`}
                                variant="outlined"
                                sx={{ ...formStyles }}
                                slotProps={{
                                  input: {
                                    endAdornment: <InputAdornment position="end">°</InputAdornment>,
                                  },
                                }}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                              />
                            )}
                          />
                        </Grid2>
                      </>
                    )}    
                  </Grid2>
                </Paper>

                {/* Elevation / Z */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {t("captures.capture.general.location-details.elevation")}
                  </Typography>
                  <Grid2 size={6}>
                    <TextField
                      fullWidth
                      label={
                        crs === "etrs89"
                          ? t("captures.capture.general.location-details.z-etrs")
                          : t("captures.capture.general.location-details.elevation")
                      }
                      variant="outlined"
                      sx={{ ...formStyles }}
                      type="number"
                      inputProps={{ step: "any" }}
                      {...methods.register(locationKey)}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">{t("generic.units.m")}</InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Grid2>
                </Paper>
              </Paper>
            </Grid2>
          </Grid2>

          {/* Buttons */}
          <Grid2 size={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3}}>
            <Box sx={{display: "flex", gap: 2}}>
              <Button type="submit" variant="contained">
                {t("generic.create")}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(-1)}
              >
                {t("generic.cancel")}
              </Button>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}
